import {
  IonList,
  IonItem,
  IonLabel,
  IonCard,
  IonCardContent,
  IonLoading,
  RefresherEventDetail,
  IonRefresher,
  IonRefresherContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonTitle,
} from "@ionic/react";
import { add } from "ionicons/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryExec } from "../hooks/useQueryExec";
import { decodeParam } from "../util/ApiHelper";
import { formatData, getAllFields } from "../models/workorders/MeterReading";
import MeterReadingsModal from "./MeterReadingsModal";
import OnlineStatus from "./OnlineStatus";
import TestEnvBanner from "./TestEnvBanner";
import {
  TranslationsContext,
  TranslationMessagesContext,
} from "../util/Translations";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import useGetMeterMetadata from "../hooks/work-order/useGetMeterMetadata";
import { useWorkOrderStore } from "../store/wo-order";
import useGetMeterReadingHistory from "../hooks/work-order/useGetMeterReadingHistory";
import { useShallow } from "zustand/react/shallow";
import useGetNoOfMeterHistoryDisplay from "../hooks/work-order/useGetNoOfMeterHistoryDisplay";
import { useOfflineStore } from "../store/offline-mode";

const MeterReadingHistory: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { errorState } = useQueryExec(
    "Work Order",
    "Work Order",
    "cf_work_order"
  );
  const { workOrderId, meter } = useParams<{
    workOrderId: string;
    meter: string;
  }>();
  let displayMeter = decodeParam(meter);
  const { getMeterMetadata } = useGetMeterMetadata();
  const { getMeterReadingHistory } = useGetMeterReadingHistory();
  const { getNoOfMeterHistoryDisplay } = useGetNoOfMeterHistoryDisplay();

  const metadataHistory = useWorkOrderStore(
    useShallow((state) => state.getMeterHistoryMetadataForWo())
  );
  const meterReadingHistory = useWorkOrderStore(
    useShallow((state) => state.getMeterReadingsHistoryForMeter(meter))
  );
  const workOrder = useWorkOrderStore(
    useShallow((state) => state.getWorkOrderById(workOrderId))
  );
  const noOfRecords = useWorkOrderStore(
    (state) => state.noOfMeterHistoryDisplay
  );
  const isOffline = useOfflineStore((state) => state.isOffline);

  const [readingDate, setReadingDate] = useState<string>("");
  const [readingTime, setReadingTime] = useState<string>("");
  const [reading, setReading] = useState<number>(0);
  const [comments, setComments] = useState<string>("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [inRefresh, setInRefresh] = useState(false);
  const isSerial = workOrder?.WriteWoToSerialFlag === true ? "s" : "e";
  const equipSerial = workOrder?.Equipment ?? "";

  function handleRefresh(event: CustomEvent<RefresherEventDetail>) {
    setInRefresh(true);
    event.detail.complete();
  }

  const getNoOfRecords = useCallback(async () => {
    if (typeof noOfRecords !== "number") {
      await getNoOfMeterHistoryDisplay();
    }
  }, [getNoOfMeterHistoryDisplay, noOfRecords]);

  const metadataQueryHistory = useCallback(async () => {
    if (metadataHistory) return; //got it from cache
    await getMeterMetadata(true);
  }, [getMeterMetadata, metadataHistory]);

  const meterReadingQueryHistory = useCallback(
    async (refresh?: boolean) => {
      if ((meterReadingHistory || isOffline) && !refresh) return; //got it from cache
      await getMeterReadingHistory(workOrderId, displayMeter);
    },
    [
      displayMeter,
      getMeterReadingHistory,
      isOffline,
      meterReadingHistory,
      workOrderId,
    ]
  );

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      await getNoOfRecords();
      await metadataQueryHistory();
      await meterReadingQueryHistory();
      setShowLoading(false);
    })();
  }, [
    equipSerial,
    workOrderId,
    inRefresh,
    metadataQueryHistory,
    meterReadingQueryHistory,
    getNoOfRecords,
  ]);

  const onAddModalCancel = () => {
    setIsAddModalOpen(false);
  };
  const onAddModalConfirm = () => {
    setIsAddModalOpen(false);
    if (!isOffline) meterReadingQueryHistory(true);
  };

  return (
    <>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>
      <TestEnvBanner />
      <OnlineStatus />
      {isAddModalOpen && (
        <MeterReadingsModal
          isOpen={isAddModalOpen}
          cancel={onAddModalCancel}
          confirm={onAddModalConfirm}
          readDate={readingDate}
          readTime={readingTime}
          reading={reading}
          comments={comments}
          equipSerial={equipSerial}
          isSerial={isSerial}
        />
      )}
      <IonTitle
        style={{ padding: "12px", margin: "5px", display: "inline-block", position: "sticky", top:0, zIndex: 10, backgroundColor: 'var(--ion-background-color)'}}
      >
        {`${translations["lbl_category_meter"] || "Meter"}: ${displayMeter}`}
      </IonTitle>
      {!!metadataHistory &&
        !!meterReadingHistory &&
        meterReadingHistory.map((meterHistory: any, index) => {
          if (index < (noOfRecords ?? 0)) {
            return (
              <IonCard
                key={index}
                // WILL BE UNCOMMENTED ONCE WE REQUIRE EDIT FUNCTIONALITY
                // onClick={() => {
                //   setReadingDate(meterHistory.ReadingDate);
                //   setReadingTime(meterHistory.ReadingTime);
                //   setReading(meterHistory.Reading);
                //   setComments(meterHistory.Comments);
                //   setIsAddModalOpen(true);
                // }}
              >
                <IonCardContent>
                  <IonList lines="full">
                    {getAllFields(metadataHistory).map(
                      (meterHistoryField: any) => {
                        const data =
                          meterHistory[meterHistoryField.PropertyName];
                        return (
                          <IonItem key={meterHistoryField.IdText}>
                            <IonLabel>
                              {meterHistoryField.TranslatedIdText}
                            </IonLabel>
                            {meterHistoryField.PropertyName === "ReadingDate"
                              ? formatData(
                                  meterHistory.ReadingDate,
                                  meterHistoryField
                                )
                              : meterHistoryField.PropertyName === "ReadingTime"
                              ? meterHistory.ReadingTime
                              : meterHistoryField.PropertyName === "Reading"
                              ? meterHistory.Reading
                              : formatData(data, meterHistoryField)}
                          </IonItem>
                        );
                      }
                    )}
                  </IonList>
                </IonCardContent>
              </IonCard>
            );
          }
          return null;
        })}
      <IonLabel hidden={errorState === ""} color={"danger"}>
        {errorState}
      </IonLabel>
      {(meterReadingHistory ?? []).length === 0 && (
        <IonLabel
          hidden={showLoading}
          color={"warning"}
          style={{ padding: "16px", display: "inline-block" }}
        >
          {translatedMessages["NoResultsFound"]?.MessageText ||
            "No results found."}
        </IonLabel>
      )}
      <IonFab
        vertical="bottom"
        horizontal="end"
        style={{
          position: "fixed",
          bottom: "calc(16px + env(safe-area-inset-bottom))",
          right: "16px",
          zIndex: 1000,
        }}
      >
        <IonFabButton
          onClick={() => {
            setReadingDate("");
            setReadingTime("");
            setReading(0);
            setComments("");
            setIsAddModalOpen(true);
          }}
        >
          <IonIcon icon={add}></IonIcon>
        </IonFabButton>
      </IonFab>
    </>
  );
};

export default withAITracking(
  reactPlugin,
  MeterReadingHistory,
  "MeterReadingHistory"
);
